import { Fragment, useEffect, useMemo, useState, memo, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getLang, getModule, getAvailableModules } from "store/userSlice";
import { errorMessage, formatDateTime } from "helpers/common";
import { Tooltip as ReactTooltip } from "react-tooltip";
import { api } from "helpers/axios";
import sanitize from "helpers/sanitize";
import "react-tooltip/dist/react-tooltip.css";
import ErrorAlert from "components/ui/ErrorAlert";
import ImportDetailHeader from "components/ui/ImportDetailHeader";
import ImportDetailTabs from "components/ui/ImportDetailTabs";
import ImportReportMenu from "./ImportReportMenu";
import { toast } from "helpers/toast";
import { autoAssignHeaders } from "helpers/importFieldMatching";
import { DndContext, DragOverlay, useDraggable, useDroppable } from "@dnd-kit/core";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "translation/useTranslation";
import { fetchFields, getAllCustomNames, getAllFields } from "store/fieldsSlice";
import { buildImportOverviewPath, IMPORT_TABS } from "./importNavigation";
import { POLLABLE_STATUSES, buildRecordCells, getDefaultDetailTab, getDetailRecordFields, getVisibleRecordHeaders, operationAlts, operationStatusAlts } from "./importHelpers";
import { isDbModule } from "helpers/module";
import TableScrollbar from "components/EmployeeManagement/TableScrollbar";
import { Banner, Button, FilterPillGroup, Input, InputAffix, ListPagination, Modal, NativeSelect, Select } from "components/ui";
import EmployeeImportConflictReview, { EmailConflictEmployeeLink } from "./EmployeeImportConflictReview";

const normalizeImportLogFieldKey = (fieldKey) => {
  if (!fieldKey || typeof fieldKey !== "string") return "";
  return fieldKey.replace(/^user:|^employee_information:/, "");
};

const translateImportLogFieldLabel = (fieldKey, translate) => {
  const normalizedFieldKey = normalizeImportLogFieldKey(fieldKey);
  if (!normalizedFieldKey) return "";
  return translate(`field.${normalizedFieldKey}`, normalizedFieldKey.replace(/_/g, " "));
};

const buildImportLogHtml = (logItem, translate, editableTranslateString) => {
  const message = editableTranslateString(logItem.translation_key, logItem.fallback);

  if (Array.isArray(logItem.fields) && logItem.fields.length > 0) {
    const fieldLabels = logItem.fields
      .map(field => translateImportLogFieldLabel(field, translate))
      .filter(Boolean);

    if (fieldLabels.length > 0) {
      return `• ${message}: ${fieldLabels.join(", ")}`;
    }

    return `• ${message}`;
  }

  const fieldLabel = translateImportLogFieldLabel(logItem.field, translate);
  if (fieldLabel) {
    return `• <span class="mr-1">${fieldLabel}: </span>${message}`;
  }

  return `• ${message}`;
};

const PreviousMappingSelect = memo(function PreviousMappingSelect({ importData, imports, selectedPreviousImport, setSelectedPreviousImport }) {
  const lang = useSelector(getLang);
  const { translate } = useTranslation();

  const [previousImportOptionsFile, previousImportOptionsSource] = useMemo(() => {
    const baseNoOption = [{ label: translate("user_import.no"), value: "no" }];

    if (!Array.isArray(imports) || imports.length === 0) {
      return [baseNoOption, baseNoOption];
    }

    const temp = [...imports];
    temp.sort((a, b) => (b.completed_at ?? "").localeCompare(a.completed_at ?? ""));

    const arr = [
      { label: translate("user_import.no"), value: "no" },
      { label: translate("user_import.previous_imports.match_filename", "Passender Dateiname"), options: [] },
      { label: translate("user_import.previous_imports.all_previous_imports", "Alle vorherigen Importe"), options: [] }
    ];

    const arr2 = [
      { label: translate("user_import.no"), value: "no" },
      { label: translate("user_import.previous_imports.all_previous_imports", "Alle vorherigen Importe"), options: [] }
    ];

    for (const im of temp) {
      if (!im.import_scope) continue;

      const optionItem = {
        label: im.original_file_name + " (" + im.imported_records + " records) - (recent status: " + im.status + (im.completed_at ? ", completed at: " + formatDateTime(im.completed_at, lang) : "") + ")",
        value: im.hash_id
      };

      if (!im.import_source) {
        let uploadedFileName = "";
        if (!importData?.import_source) {
          uploadedFileName = importData?.original_file_name || "";
        }
        const imFileNameWithoutExt = im.original_file_name.replace(/\.[^/.]+$/, "");
        if (uploadedFileName.includes(imFileNameWithoutExt)) {
          arr[1].options.push(optionItem);
          continue;
        }
        arr[2].options.push(optionItem);
      } else {
        arr2[1].options.push(optionItem);
      }
    }

    return [arr, arr2];
  }, [imports, importData]);

  if (!importData) return null;

  const options = importData.import_source ? previousImportOptionsSource : previousImportOptionsFile;
  const defaultValue = importData.import_source ? previousImportOptionsSource[0] : previousImportOptionsFile[0];
  const value = selectedPreviousImport || defaultValue;

  return <div style={{ display: 'contents' }} data-testid="import-field-select">
    <Select className="w-50" placeholder={translate('form.select_placeholder')} options={options} defaultValue={defaultValue} value={value} onChange={val => setSelectedPreviousImport(val)} />
  </div>;
});

const ImportRecordsTable = memo(function ImportRecordsTable({
  importStatus,
  overviewImportRecordsTableHeaders,
  importRecordsSortCol,
  handleChangeImportRecordsSortCol,
  importRecordsPaged,
  selectedImportRecord,
  setSelectedImportRecord,
  importRecordErrorFields,
  importRecordMetadata,
  detailedImportTableData,
  detailedImportTableHeaders,
  formatLog,
  resolvingRecordHash,
  onResolveConflict,
  conflictResolutionCapabilities,
}) {
  const { editableTranslate } = useTranslation()

  const [resizedCount, setResizedCount] = useState(0);

  const tableRef = useRef(null);

  const showLogColumnGlobal = ["completed", "partially_completed", "failed"].includes(importStatus);

  useEffect(() => {
    setResizedCount(c => c + 1);
  }, [overviewImportRecordsTableHeaders, importRecordsPaged]);

  if (!overviewImportRecordsTableHeaders.length || !importRecordsPaged.length) return null;

  const renderCellContent = (field, j, len, showLogColumn, headers, recordMetadata) => {
    if (j === len - 1 && showLogColumn) {
      const emailConflict = recordMetadata?.meta?.email_conflict;
      if (emailConflict?.employee_hash_id && emailConflict?.employee_number) {
        return <span>
          • {editableTranslate("user_import.conflict.email.title", "E-Mail bereits vergeben")}: <EmailConflictEmployeeLink
            conflict={emailConflict}
            onClick={event => event.stopPropagation()}
          />{emailConflict.employee_name && ` (Personalnummer ${emailConflict.employee_number})`}.
        </span>;
      }
      return <span dangerouslySetInnerHTML={{ __html: sanitize(formatLog(field)) }} />;
    }
    if (j === len - 2) {
      const badgeClass = field.includes("imported") ? "badge-success" : field.includes("failed") ? "badge-danger" : "badge-warning";
      const arr = field?.split(': ') || [];
      if (arr.length === 2) {
        return <span className={"badge " + badgeClass + " text-capitalize"}>{editableTranslate('user_import.operation.' + arr[0], operationAlts[arr[0]] ?? '')}: {editableTranslate('user_import.operation_status.' + arr[1], operationStatusAlts[arr[1]] ?? '')}</span>;
      } else {
        return <></>;
      }
    }
    return headers[j].field.includes("date") ? formatDateTime(field, true, false) : field;
  };

  return (
    <>
      <hr />
      <div className="table-responsive has-custom-scrollbar">
        <table className="table table-fixed common-table import-records-table table-hover" ref={tableRef} data-testid="import-records-paged-table">
          <thead>
            <tr>
              <th className="text-center width-35"></th>
              {overviewImportRecordsTableHeaders.map((header, i) => {
                if (header.field === "table_log" && !["completed", "partially_completed", "failed"].includes(importStatus)) {
                  return null;
                }
                return (
                  <th key={i}>
                    <span data-testid={`header-${header.field}`} className="cursor-pointer" onClick={() => handleChangeImportRecordsSortCol(i)}>
                      {header.label}
                      <i className={"bi bi-bold " + (importRecordsSortCol[0] === i ? (importRecordsSortCol[1] === "asc" ? "bi-arrow-up" : "bi-arrow-down") : "bi-arrow-up invisible") + " ml-1"}></i>
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {importRecordsPaged.map(record => {
              const isSelected = selectedImportRecord?.hash_id === record.hash_id;
              const recordMetadata = importRecordMetadata[record.hash_id];
              const fields = record;
              const len = fields.length;
              const showLogColumn = showLogColumnGlobal;

              return (
                <Fragment key={record.hash_id}>
                  <tr className={"cursor-pointer " + (isSelected ? "selected-table-row" : "")} onClick={() => setSelectedImportRecord(isSelected ? null : record)} data-testid={`row-${record?.hash_id}`}>
                    <td className="text-center v-middle width-35">
                      <i className={"open-row-icon bi bi-chevron-" + (isSelected ? "up" : "down")} />
                    </td>
                    {fields.map((field, j) => {
                      if (j === len - 1 && !showLogColumn) return null;
                      return (
                        <td key={record.hash_id + "-" + j} colSpan={j === len - 1 && len < overviewImportRecordsTableHeaders.length ? overviewImportRecordsTableHeaders.length - len + 1 : 1} className={importRecordErrorFields[record.hash_id]?.includes(overviewImportRecordsTableHeaders[j].field) ? "text-red" : ""}>
                          {renderCellContent(field, j, len, showLogColumn, overviewImportRecordsTableHeaders, recordMetadata)}
                        </td>
                      );
                    })}
                  </tr>
                  {isSelected && (
                    <tr className="detail-row">
                      <td colSpan={(showLogColumn ? len + 1 : len) + (overviewImportRecordsTableHeaders.length - len)}>
                        <div className="detail-div">
                          <div className="bg-white p-3">
                            <div className="general-info">
                              <div className="row row-cols-4">
                                {(detailedImportTableData.find(d => d.hash_id === record.hash_id)?.records || []).map((data, k) => (
                                  <div key={k} className={"col " + (k > 3 ? "mt-3" : "")}>
                                    <span>{detailedImportTableHeaders[k].label}</span>
                                    <span className={"info-field-value " + (importRecordErrorFields[record.hash_id]?.includes(detailedImportTableHeaders[k].field) ? "text-red" : "")}>{data || "-"}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                            <EmployeeImportConflictReview
                              record={recordMetadata}
                              capabilities={conflictResolutionCapabilities}
                              resolving={resolvingRecordHash === record.hash_id}
                              onResolve={onResolveConflict}
                            />
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      <TableScrollbar tableRef={tableRef} data={importRecordsPaged} trigger={resizedCount} />
    </>
  );
});

function GroupedScopeField({ section, fields, fieldMapping,
  handleManualRemove,
  onChangeWorkingHoursSwitch,
  dateFormats,
  importStatus,
  extraData,
  onChangeExtraData,
  overwriteFields,
  handleCheckOverwrite,
  availableModules }) {
  const { editableTranslate } = useTranslation();

  const [show, setShow] = useState(true);

  return <div className="group-category-div">
    <div data-testid={`section-toggle-${section}`} onClick={() => setShow(!show)} className="category-label-div cursor-pointer">
      <span className="font-weight-bold ml-1">
        <i className={`bi bi-chevron-${show ? 'down' : 'right'} mr-2`}></i>
        {editableTranslate(`user_import.${section}`, section)}
      </span>
    </div>
    {
      show && <div className="group-category-detail">
        <div className="row">
          {fields.map((f, i) => (
            <div key={i} className="mb-2 col-xxl-3 col-xl-4 col-lg-6 pt-2">
              <Droppable field={f} item={fieldMapping?.[f.key]} handleManualRemove={handleManualRemove}
                onChangeWorkingHoursSwitch={onChangeWorkingHoursSwitch}
                dateFormats={dateFormats}
                importStatus={importStatus}
                extraData={extraData}
                onChangeExtraData={onChangeExtraData}
                overwriteFields={overwriteFields}
                handleCheckOverwrite={handleCheckOverwrite}
                availableModules={availableModules} />
            </div>
          ))}
        </div>
      </div>
    }
  </div>;
}

function Draggable({ id, originalId = id, active = false, handleManualRemove = null, fieldMapping = null }) {
  const { isDragging, attributes, listeners, setNodeRef, transform } = useDraggable({ id });

  const counter = useMemo(() => {
    if (!fieldMapping) return 0;

    let count = 0;
    for (const k in fieldMapping) {
      if (fieldMapping[k] === originalId) count++;
    }
    return count;
  }, [fieldMapping]);

  const style = {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    opacity: isDragging ? 0 : 1
  };

  return (
    <div className={`import-draggable ${active ? 'active' : ''} ${counter > 0 ? 'has-count' : ''}`} ref={setNodeRef} style={style} {...listeners} {...attributes}>
      <span>{originalId}</span>

      {
        counter > 1 && <span className="counter-span">{counter}</span>
      }

      {
        active && <Button variant="ghost" size="inline" data-no-dnd="true" icon="bi-x" onPointerDown={(e) => e.stopPropagation()} onClick={handleManualRemove} data-testid={`remove-mapping-${originalId}`} />
      }
    </div>
  );
}

export function Droppable({ field, item, handleManualRemove, onChangeWorkingHoursSwitch, extraData, importStatus, dateFormats, onChangeExtraData, overwriteFields, handleCheckOverwrite, availableModules }) {
  const { translate, editableTranslate } = useTranslation();
  const groupOverwriteTooltipId = `import-group-overwrite-tooltip-${field.key}`;
  const isWorkingHoursInHours = extraData?.working_hours?.working_hours_type === "in_hours";

  const { isOver, setNodeRef } = useDroppable({ id: field.key });

  return (
    <div className={`import-droppable ${isOver ? 'on-hover' : ''} ${item ? 'has-item' : ''}`} ref={setNodeRef}>
      <div className="import-droppable-header">
        <span>{field?.custom_name || editableTranslate(field.translation_key, field.label)}</span>
        {
          field.required && <span className="required-badge">{editableTranslate('user_import.required', 'PFLICHT')}</span>
        }
      </div>

      <div className="import-droppable-value">
        {item ? <Draggable id={`slot-${field.key}-${item}`} originalId={item} active={true} handleManualRemove={() => handleManualRemove(field.key, item)} /> : editableTranslate('user_import.drag_field_name_here', 'Feldname hierher ziehen...')}
      </div>

      {
        item && <div>
          {
            (field.key === "working_hours"
              || (["group", "employee_group"].includes(field.key))
              || field.key.includes("date")
              || field?.allow_overwrite) && <hr />
          }
          {
            field.key === "working_hours" && <div>
              <div className="d-flex flex-wrap gap-2 align-items-center">
                <span>{editableTranslate('user_import.unit', 'Einheit')}:</span>
                <div className="d-flex flex-wrap align-items-center justify-content-left gap-1">
                  <div className={"d-flex " + (isWorkingHoursInHours ? "mr-3" : "")}>
                    <div className="d-flex align-items-center ml-3">
                      <span>{editableTranslate("general_settings.working_hours_type.percentage")}</span>
                      <span className="toggle-switch inverse mx-1">
                        <input id="working-hours-switch" type="checkbox" checked={isWorkingHoursInHours} onChange={onChangeWorkingHoursSwitch} disabled={importStatus !== "draft"} data-testid="working-hours-switch-checkbox" />
                        <label htmlFor="working-hours-switch"></label>
                      </span>
                      <span>{editableTranslate("general_settings.working_hours_type.hours")}</span>
                    </div>
                  </div>
                </div>
                {isWorkingHoursInHours && (
                  <div className="d-flex align-items-center">
                    <InputAffix className="flex-nowrap w-auto" disabled={importStatus !== "draft"} suffix={editableTranslate("general_settings.working_hours_type.hours")}>
                      <Input type="text" className="max-w-100 mr-2" style={{ flex: "none" }} disabled={importStatus !== "draft"} name="default_working_hours" defaultValue={extraData?.working_hours?.default_working_hours} maxLength={24} placeholder={translate("user_import.default_working_hours", "Standardarbeitszeit")} onChange={e => onChangeExtraData(field.key, e)} data-testid="default-working-hours-input" />
                    </InputAffix>
                    <i className="bi bi-info-circle text-primary ml-2" data-tooltip-id="default-working-hours-tooltip" data-testid="default-working-hours-info"></i>
                    <ReactTooltip id="default-working-hours-tooltip" place="bottom" content={translate('user_import.default_working_hours_info', 'Nur der Teiler für die Umrechnung von Stunden in Prozent, zum Beispiel 19,5 von 39 Std. = 50 %. Leere Werte werden nicht gefüllt.')} />
                  </div>
                )}
              </div>
              {/* LIX-1767 hard-coded Arbeitszeit help; move to editable help content once the tooltip/help editing is reworked */}
              <div className="mt-2 small text-body-color" data-testid="working-hours-help">
                <strong>{translate('user_import.working_hours_help_title', 'Hinweis zur Arbeitszeit')}</strong>
                <ul className="mb-0">
                  <li>{translate('user_import.working_hours_help_percent', 'Prozent: Die Werte aus der Datei werden direkt als Prozentwert übernommen.')}</li>
                  <li>{translate('user_import.working_hours_help_hours', 'Stunden: Die regulären Wochenstunden sind nur der Teiler für die Umrechnung, zum Beispiel 19,5 von 39 Std. = 50 %.')}</li>
                  <li>{translate('user_import.working_hours_help_empty', 'Eine leere Zelle oder eine fehlende Spalte lässt die Arbeitszeit bei neuen Personen leer. Bei einer Aktualisierung mit aktivem Überschreiben löscht eine leere Zelle den vorhandenen Wert.')}</li>
                </ul>
              </div>
            </div>
          }
          {
            field.key !== "working_hours" && <div>
              {
                (["group", "employee_group", "reference_group_name"].includes(field.key)) && <div className="form-check">
                  <label className="form-check-label">
                    <input className="form-check-input" type="checkbox" onChange={e => onChangeExtraData(field.key, e)} name="create_if_not_exist" defaultChecked={extraData?.[field.key]?.create_if_not_exist || false} value={true} disabled={importStatus !== "draft"} data-testid="create-if-not-exist-checkbox" />
                    <span className="ml-1">
                      {editableTranslate('user_import.create_if_does_not_exist', 'Erstellen, falls nicht vorhanden')}
                      <i className="bi bi-info-circle ml-1 text-primary" data-tooltip-id={groupOverwriteTooltipId}></i>
                    </span>
                  </label>
                  <ReactTooltip id={groupOverwriteTooltipId} place="bottom" content={translate('user_import.group_not_overwritable_info', 'Gruppen können erstellt werden, wenn sie nicht vorhanden sind, bestehende Gruppen können beim Import jedoch nicht überschrieben werden.')} />
                </div>
              }
              {
                field.key.includes("date") && <div>
                  <span>{editableTranslate('user_import.format', 'Format')}:</span>
                  <NativeSelect className="d-inline-block w-auto ml-2" name="date_format" onChange={e => onChangeExtraData(field.key, e)} disabled={importStatus !== "draft"} value={extraData?.[field.key]?.date_format || null} data-testid="date-format-select">
                    {Object.keys(dateFormats).map((item, index) => (
                      <option key={index} value={dateFormats[item]}>{item}</option>
                    ))}
                  </NativeSelect>
                </div>
              }
            </div>
          }

          {
            (availableModules.length === 2 && (['pay_grade', 'group'].includes(field.key) && field.section !== 'state_assessment_data')) && <div className="form-check mt-075">
              <label className="form-check-label">
                <input className="form-check-input" type="checkbox" onChange={e => onChangeExtraData(field.key, e)} name="also_for_other_module" defaultChecked={extraData?.[field.key]?.also_for_other_module || false} value={true} disabled={importStatus !== "draft"} data-testid="also-for-other-module-checkbox" />
                <span className="ml-1">{editableTranslate('user_import.also_for_other_module')} <i className="bi bi-info-circle ml-1" data-tooltip-id="import-also-for-other-module-tooltip"></i></span>
              </label>
            </div>
          }

          {
            field?.allow_overwrite && <div className="form-check mt-075">
              <label className="form-check-label">
                <input className="form-check-input" type="checkbox" value={field.key} checked={overwriteFields[field.key] || false} onChange={e => handleCheckOverwrite(field.key, e.target.checked)} disabled={importStatus !== "draft"} data-testid={`import-overwrite-${field.key}`} />
                <span className="ml-1">{editableTranslate('user_import.overwrite_if_confict', 'Überschreiben bei Konflikt')}</span>
              </label>
            </div>
          }
        </div>
      }
    </div>
  );
}

export default function EmployeeImportForm({ id, setMode = null, forTable = false, fetchUserImports = null, imports = [], helps, backTo = null, initialRecordFilter = "all" }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { translate, editableTranslate, editableTranslateString } = useTranslation();

  const availableModules = useSelector(getAvailableModules);

  const allFields = useSelector(getAllFields);
  const fieldCustomNames = useSelector(getAllCustomNames);

  const currentModule = useSelector(getModule);

  const isDbModuleActive = useMemo(() => {
    return isDbModule(currentModule);
  }, [currentModule]);

  const dateFormats = {
    "dd.mm.yyyy": "d.m.Y",
    "dd.mm.yy": "d.m.y",
    "dd/mm/yyyy": "d/m/Y",
    "dd-mm-yyyy": "d-m-Y",
    "mm/dd/yyyy": "m/d/Y",
    "mm-dd-yyyy": "m-d-Y",
    "mm.dd.yyyy": "m.d.Y",
    "yyyy/mm/dd": "Y/m/d",
    "yyyy-mm-dd": "Y-m-d",
    "yyyy.mm.dd": "Y.m.d"
  };

  const [loading, setLoading] = useState(false);
  const [loadingImportData, setLoadingImportData] = useState(false);
  const [loadingRecords, setLoadingRecords] = useState(false);
  const [error, setError] = useState("");

  const [importData, setImportData] = useState(null);
  const [importStatus, setImportStatus] = useState(null);
  const [fieldMapping, setFieldMapping] = useState(null);
  const [inScopeFields, setInScopeFields] = useState(null);

  const [fieldMappingError, setFieldMappingError] = useState("");

  const [extraData, setExtraData] = useState({
    employment_entry_date: { date_format: dateFormats[Object.keys(dateFormats)[0]] },
    employment_exit_date: { date_format: dateFormats[Object.keys(dateFormats)[0]] }
  });

  const [overviewImportRecords, setOverviewImportRecords] = useState(null);
  const [recordTableBaseHeaders, setRecordTableBaseHeaders] = useState([]);
  const overviewImportRecordsTableHeaders = useMemo(
    () => getVisibleRecordHeaders(recordTableBaseHeaders, fieldMapping, inScopeFields),
    [recordTableBaseHeaders, fieldMapping, inScopeFields]
  );
  const [selectedImportRecord, setSelectedImportRecord] = useState(null);
  const [detailedImportTableHeaders, setDetailedImportTableHeaders] = useState([]);
  const [detailedImportTableData, setDetailedImportTableData] = useState([]);
  const [importRecordErrorFields, setImportRecordErrorFields] = useState({});
  const [importRecordMetadata, setImportRecordMetadata] = useState({});
  const [recordView, setRecordView] = useState(initialRecordFilter);
  const [resolutionDialog, setResolutionDialog] = useState(null);
  const [resolvingRecordHash, setResolvingRecordHash] = useState(null);

  const [importRecordsSortCol, setImportRecordsSortCol] = useState([0, "asc"]);
  const [importRecordsPagination, setImportRecordsPagination] = useState({ page: 1, totalRows: 0, totalPages: 1, perPage: 20 });
  const importRecordsRequestId = useRef(0);

  const [overwriteFields, setOverwriteFields] = useState({});

  const [availableScopeFields, setAvailableScopeFields] = useState([]);

  const [selectedPreviousImport, setSelectedPreviousImport] = useState(null);
  const [previousImports, setPreviousImports] = useState(imports);
  const [previousImportsLoaded, setPreviousImportsLoaded] = useState(imports.length > 0);
  const [activeDetailTab, setActiveDetailTab] = useState("details");
  const detailTabPickedRef = useRef(false);

  const [activeId, setActiveId] = useState(null);
  const handleReturnToOverview = useCallback(() => {
    if (typeof backTo === "string") {
      navigate(backTo);
      return;
    }

    if (setMode) {
      setMode("view");
      return;
    }

    navigate(buildImportOverviewPath(`tab=${IMPORT_TABS.employees}`));
  }, [backTo, navigate, setMode]);

  const duplicatedFieldsOnState = ['employee_information:state_employee_pay_grade', 'working_hours', 'employee_group'];

  const [groupedScopeFields, filteredAvailableScopeFields] = useMemo(() => {
    let filtered = [];

    const groupedFields = new Map();

    availableScopeFields.forEach((field) => {
      if (!field.section) return;

      if (!groupedFields.has(field.section)) {
        groupedFields.set(field.section, []);
      }

      if (availableModules.length === 1 || (field?.section !== 'state_assessment_data' || !duplicatedFieldsOnState.includes(field.key))) {
        groupedFields.get(field.section).push(field);
        filtered.push(field);
      }
    });

    if (availableModules.length === 1 && isDbModule(availableModules[0])) {
      groupedFields.delete('evaluation_data');
      filtered = filtered.filter(f => f.section !== 'evaluation_data');
    }

    return [
      Array.from(groupedFields, ([section, fields]) => ({ section, fields })),
      filtered
    ];
  }, [availableScopeFields, duplicatedFieldsOnState, availableModules]);

  useEffect(() => {
    if (!allFields) dispatch(fetchFields());

    fetchImportData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setPreviousImports(imports);
    setPreviousImportsLoaded(imports.length > 0);
  }, [imports]);

  // Follow the status default only until the user picks a tab, so reloads and polling don't move them.
  useEffect(() => {
    if (!forTable && importStatus && !detailTabPickedRef.current) {
      setActiveDetailTab(getDefaultDetailTab(importStatus));
    }
  }, [forTable, importStatus]);

  const handleDetailTabChange = tab => {
    detailTabPickedRef.current = true;
    setActiveDetailTab(tab);
  };

  useEffect(() => {
    setRecordTableBaseHeaders([
      { field: "user:first_name", label: fieldCustomNames?.['field.first_name'] || editableTranslate("field.first_name", "Vorname") },
      { field: "user:last_name", label: fieldCustomNames?.['field.last_name'] || editableTranslate("field.last_name", "Nachname") },
      { field: "employee_number", label: fieldCustomNames?.['field.employee_number'] || editableTranslate("field.employee_number", "Personalnummer") },
      { field: "table_status", label: editableTranslate("user_import.status", "Status") },
      { field: "table_log", label: editableTranslate("user_import.log", "Protokoll") }
    ]);
  }, [fieldCustomNames]);

  useEffect(() => {
    if (!selectedPreviousImport) return;
    updatePreviousImport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPreviousImport]);

  useEffect(() => {
    fetchAvailableFields();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [importData]);

  useEffect(() => {
    if (fieldMapping && inScopeFields && importStatus !== "draft") {
      fetchImportRecords();
    }

    if (fieldMappingError && fieldMapping && inScopeFields && Object.keys(fieldMapping).length === inScopeFields.length) {
      setFieldMappingError("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldMapping, inScopeFields, importStatus]);

  useEffect(() => {
    if (importStatus === "draft" && !previousImportsLoaded) {
      fetchPreviousImports();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [importStatus, previousImportsLoaded]);

  const importRecordsPaged = overviewImportRecords || [];
  const skippedImportRecordsCount = importData?.skipped_records_count ?? 0;
  const issueImportRecordsCount = importData?.recoverable_records_count ?? 0;

  const unresolvedConflictCount = useMemo(() => (
    importData?.unresolved_conflicted_records_count
    ?? Object.values(importRecordMetadata).filter(record => record.status === "conflicted" && !record.conflict_resolution).length
  ), [importData, importRecordMetadata]);

  const isProcessing = ["scheduled", "importing"].includes(importStatus);
  const detailStats = useMemo(() => {
    const stats = [
      {
        label: editableTranslate("user_import.total_records", "Anzahl der Datensätze"),
        value: importData?.total_records ?? "-",
      },
      {
        label: editableTranslate("user_import.imported_records", "Importierte Datensätze"),
        value: importData?.applied_records_count ?? importData?.imported_records ?? 0,
        valueClassName: "text-success",
      },
    ];

    if (isProcessing) {
      stats.push({
        label: editableTranslate("user_import.progress", "Fortschritt"),
        value: `${Math.min(Math.max(Math.round(importData?.progress ?? 0), 0), 100)}%`,
      });
    } else if (unresolvedConflictCount > 0) {
      stats.push({
        label: editableTranslate("user_import.unresolved_conflicts", "Offene Konflikte"),
        value: unresolvedConflictCount,
        valueClassName: "text-danger",
      });
    } else if (skippedImportRecordsCount > 0) {
      stats.push({
        label: editableTranslate("user_import.skipped", "Verworfen"),
        value: skippedImportRecordsCount,
        valueClassName: "text-warning",
      });
    } else {
      stats.push({
        label: editableTranslate("user_import.failed_records", "Fehlerhaft"),
        value: issueImportRecordsCount,
        valueClassName: "text-danger",
      });
    }

    return stats;
  }, [editableTranslate, importData, isProcessing, issueImportRecordsCount, skippedImportRecordsCount, unresolvedConflictCount]);

  const reverseFieldMapping = useMemo(() => {
    if (!fieldMapping || availableScopeFields.length === 0) return [];

    const arr = [];

    for (const key in fieldMapping) {
      const header = fieldMapping[key];
      if (header) {
        const field = availableScopeFields.find(f => f.key === key);
        if (!field) continue;

        let index = -1;
        for (let i = 0; i < arr.length; i++) {
          if (arr[i].header === header) {
            index = i;
            break;
          }
        }
        if (index === -1) {
          arr.push({
            header,
            fields: [field?.custom_name || translate(field.translation_key, field.label)]
          });
        } else {
          arr[index].fields.push(field?.custom_name || translate(field.translation_key, field.label));
        }
      }
    }

    return arr;
  }, [fieldMapping, isDbModuleActive, availableScopeFields]);

  const parseDraggableId = (id) => {
    if (id.startsWith('source-')) {
      return {
        fieldKey: null,
        originalId: id.replace('source-', '')
      };
    }

    if (id.startsWith('slot-')) {
      const [, fieldKey, ...rest] = id.split('-');
      return {
        fieldKey,
        originalId: rest.join('-')
      };
    }

    return {
      fieldKey: null,
      originalId: id
    };
  };

  const handleDragEnd = ({ active, over }) => {
    if (!over) return;

    const { fieldKey, originalId } = parseDraggableId(active?.id);

    setFieldMapping(prev => {
      const updated = { ...prev };

      if (active?.id?.startsWith('slot-')) {
        if (updated[fieldKey] === originalId) {
          updated[fieldKey] = null;
        }
      }

      updated[over.id] = originalId;

      return updated;
    });
  }

  const handleManualRemove = (fieldKey, id) => {
    setFieldMapping(prev => {
      const updated = { ...prev };
      if (updated[fieldKey] === id) {
        updated[fieldKey] = null;
      }
      return updated;
    });
  };

  const handleAutoAssign = (e) => {
    e?.currentTarget?.blur();

    const fieldMappingKeyed = autoAssignHeaders(
      filteredAvailableScopeFields,
      importData?.headers ?? [],
      translate
    );

    setFieldMapping(prev => ({
      ...(prev || {}),
      ...fieldMappingKeyed
    }));
  };

  const updatePreviousImport = async () => {
    if (!importData) return;
    setLoadingImportData(true);
    try {
      const { status } = await api.post(`/employee-imports/${importData?.hash_id}/copy-previous`, { previous_import_hash_id: selectedPreviousImport?.value === "no" ? null : selectedPreviousImport?.value });
      if (status === 200) {
        toast.success(translate("form.success.saved"));
        fetchImportData();
      }
    } catch (err) {
      setError(errorMessage(err, "save use previous mapping"));
      setLoadingImportData(false);
    }
  };

  const fetchAvailableFields = async () => {
    try {
      const { data, status } = await api.get(`/employee-imports/available-fields`);
      if (status === 200) {
        setAvailableScopeFields(data.data);
      }
    } catch (err) {
      setError(errorMessage(err, "fetchAvailableFields"));
    }
  };

  const fetchPreviousImports = async () => {
    try {
      const { data, status } = await api.get(`/employee-imports?limit=100&page=1`);
      if (status === 200) {
        setPreviousImports(data.data || []);
        setPreviousImportsLoaded(true);
      }
    } catch (err) {
      setPreviousImportsLoaded(true);
      setError(errorMessage(err, "fetchPreviousImports"));
    }
  };

  const fetchImportData = useCallback(async ({ silent = false, background = false } = {}) => {
    if (!silent) setLoadingImportData(true);
    try {
      const [importResponse, availableFieldsResponse] = await Promise.allSettled([
        api.get(`/employee-imports/${id}`, { meta: { background } }),
        api.get(`/employee-imports/available-fields`, { meta: { background } }),
      ]);

      if (importResponse.status !== "fulfilled") {
        throw importResponse.reason;
      }

      const { data, status } = importResponse.value;

      if (status === 200) {
        const availableFields = availableFieldsResponse.status === "fulfilled" && availableFieldsResponse.value?.status === 200
          ? availableFieldsResponse.value.data.data
          : [];
        const scopeFieldDefinitions = (data.import_scope?.fields || []).map(scopeField => (
          availableFields.find(field => field.key === scopeField.key) || scopeField
        ));

        setImportStatus(data.status);

        const persistedFieldMapping = Array.isArray(data.field_mapping) ? structuredClone(data.field_mapping) : [];
        const fieldMappingKeyed = {};
        const newOverwriteFields = {};
        const extraDatas = { ...extraData };

        // data.field_mapping must be sorted by overviewImportRecordsTableHeaders order first, eg: first_name, last_name, employee_number, ...
        persistedFieldMapping.sort((a, b) => {
          const headerOrder = overviewImportRecordsTableHeaders.map(h => h.field);
          const aIndex = headerOrder.indexOf(a.field);
          const bIndex = headerOrder.indexOf(b.field);
          return aIndex - bIndex;
        });

        persistedFieldMapping.forEach(mapping => {
          fieldMappingKeyed[mapping.field] = mapping.header;
          if (mapping.overwrite) newOverwriteFields[mapping.field] = true;
          if (mapping.extra_data) {
            extraDatas[mapping.field] = mapping.extra_data;
          }
        });

        if (data.status === "draft") {
          const overwriteCandidates = availableFields.length > 0 ? availableFields : scopeFieldDefinitions;

          overwriteCandidates.forEach(scopeField => {
            const scopeFieldKey = typeof scopeField === "string" ? scopeField : scopeField?.key;

            if (!scopeFieldKey) {
              return;
            }

            const hasPersistedOverwrite = persistedFieldMapping.some(
              mapping => mapping.field === scopeFieldKey && Object.prototype.hasOwnProperty.call(mapping, 'overwrite')
            );

            if (scopeField?.allow_overwrite && !hasPersistedOverwrite) {
              newOverwriteFields[scopeFieldKey] = true;
            }
          });
        }

        setExtraData(extraDatas);
        setInScopeFields(scopeFieldDefinitions);
        setAvailableScopeFields(availableFields);
        setImportData(data);
        setOverwriteFields(newOverwriteFields);

        if (data.status === "draft") {
          const draftAutoMapped = autoAssignHeaders(
            scopeFieldDefinitions,
            data.headers ?? [],
            translate
          );
          for (const [key, header] of Object.entries(draftAutoMapped)) {
            if (!fieldMappingKeyed[key]) {
              fieldMappingKeyed[key] = header;
            }
          }
        }

        if (data.status !== "draft" || (selectedPreviousImport && selectedPreviousImport?.value !== "no")) {
          setFieldMapping(prev => ({
            ...(prev || {}),           // keep what user already picked locally
            ...fieldMappingKeyed       // server wins for fields it knows about
          }));
        }
      }
    } catch (err) {
      setError(errorMessage(err, "fetchImportData"));
    }
    if (!silent) setLoadingImportData(false);
  }, [extraData, id, overviewImportRecordsTableHeaders, selectedPreviousImport, translate]);

  const fetchImportRecords = useCallback(async ({
    silent = false,
    page = importRecordsPagination.page,
    perPage = importRecordsPagination.perPage,
    view = recordView,
    sortCol = importRecordsSortCol,
  } = {}) => {
    const requestId = ++importRecordsRequestId.current;
    if (!silent) setLoadingRecords(true);
    try {
      const selectedSortField = overviewImportRecordsTableHeaders[sortCol[0]]?.field;
      const sortField = selectedSortField === "table_status"
        ? "status"
        : selectedSortField === "table_log"
          ? "log"
          : fieldMapping?.[selectedSortField];
      const params = {
        paginate: 1,
        page,
        limit: perPage,
        view,
        sort_direction: sortCol[1],
        ...(sortField ? { sort_field: sortField } : {}),
      };
      let response = await api.get(`/employee-imports/${id}/records`, { params });
      let { data, status } = response;

      const firstPagination = data?.meta?.pagination;
      if (status === 200 && data.data.length === 0 && page > 1 && firstPagination?.total_pages > 0 && page > firstPagination.total_pages) {
        params.page = firstPagination.total_pages;
        response = await api.get(`/employee-imports/${id}/records`, { params });
        data = response.data;
        status = response.status;
      }

      if (requestId !== importRecordsRequestId.current) return;

      if (status === 200) {
        const detailFields = getDetailRecordFields(recordTableBaseHeaders, fieldMapping, inScopeFields);
        setDetailedImportTableHeaders(detailFields.map(f => ({
          field: f.key,
          label: f.custom_name || editableTranslate(f.translation_key, f.label),
        })));

        const errorFieldsObj = {};
        const recordMetadata = {};
        const overviewRecords = [];
        const detailedRecords = [];

        data.data.forEach(record => {
          const recordData = record.data;

          const detailedRecord = detailFields.map(f => recordData?.[fieldMapping[f.key]] || "-");

          // Record logs are arrays of translation entries; legacy imports may still return strings.
          let recordLog = record.log;
          if (Array.isArray(record.log)) {
            const logArr = record.log.map(logItem => buildImportLogHtml(logItem, translate, editableTranslateString));
            recordLog = logArr.length > 0 ? logArr.join("<br>") : "";
          } else if (typeof record.log === "string") {
            // do nothing, already a string
          } else {
            recordLog = "";
          }

          const overviewRecord = buildRecordCells(recordData, fieldMapping, overviewImportRecordsTableHeaders, {
            status: record.operation + ': ' + record.status,
            log: recordLog || "-",
          });
          overviewRecord.hash_id = record.hash_id;

          overviewRecords.push(overviewRecord);
          detailedRecords.push({ records: detailedRecord, hash_id: record.hash_id });

          errorFieldsObj[record.hash_id] = record.error_fields;
          recordMetadata[record.hash_id] = {
            hash_id: record.hash_id,
            status: record.status,
            operation: record.operation,
            conflict_resolution: record.conflict_resolution,
            meta: record.meta,
            imported_identity: {
              employee_number: recordData?.[fieldMapping?.employee_number] ?? null,
              first_name: recordData?.[fieldMapping?.["user:first_name"]] ?? null,
              last_name: recordData?.[fieldMapping?.["user:last_name"]] ?? null,
            },
          };
        });

        const pagination = data.meta?.pagination;
        setImportRecordsPagination(prev => ({
          ...prev,
          page: pagination?.current_page ?? params.page,
          totalRows: pagination?.total ?? overviewRecords.length,
          totalPages: Math.max(1, pagination?.total_pages ?? 1),
          perPage: pagination?.per_page ?? prev.perPage,
        }));
        setOverviewImportRecords(overviewRecords);
        setDetailedImportTableData(detailedRecords);
        setImportRecordErrorFields(errorFieldsObj);
        setImportRecordMetadata(recordMetadata);
      }
    } catch (err) {
      if (requestId !== importRecordsRequestId.current) return;
      setImportRecordsPagination(prev => ({ ...prev, page: 1, totalRows: 0, totalPages: 1 }));
      setError(errorMessage(err, "fetchImportRecords"));
    }
    if (requestId === importRecordsRequestId.current) setLoadingRecords(false);
  }, [editableTranslate, editableTranslateString, fieldMapping, id, importRecordsPagination.page, importRecordsPagination.perPage, importRecordsSortCol, inScopeFields, overviewImportRecordsTableHeaders, recordTableBaseHeaders, recordView, translate]);

  useEffect(() => {
    setRecordView(initialRecordFilter);
    if (fieldMapping && inScopeFields && importStatus !== "draft") {
      fetchImportRecords({ page: 1, view: initialRecordFilter });
    }
    // Only route-driven filter changes belong here; import initialization is handled above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialRecordFilter]);

  const handleRequestConflictResolution = useCallback((recordHashId, resolution) => {
    setResolutionDialog({ recordHashId, resolution });
  }, []);

  const handleConfirmConflictResolution = useCallback(async () => {
    if (!resolutionDialog) return;

    const { recordHashId, resolution } = resolutionDialog;
    setResolvingRecordHash(recordHashId);
    try {
      await api.post(`/employee-imports/${id}/records/${recordHashId}/resolve`, { resolution });
      setResolutionDialog(null);
      setSelectedImportRecord(null);
      await Promise.all([
        fetchImportData({ silent: true }),
        fetchImportRecords({ silent: true }),
      ]);
      toast.success(translate("form.success.saved"));
    } catch (err) {
      setError(errorMessage(err, "resolve employee import conflict"));
    } finally {
      setResolvingRecordHash(null);
    }
  }, [fetchImportData, fetchImportRecords, id, resolutionDialog, translate]);

  useEffect(() => {
    if (!importStatus || !POLLABLE_STATUSES.includes(importStatus)) return;

    const intervalId = setInterval(() => {
      fetchImportData({ silent: true, background: true });
    }, 5000);

    return () => clearInterval(intervalId);
  }, [fetchImportData, importStatus]);

  const onChangeExtraData = useCallback((fieldKey, e) => {
    const name = e.target.name;
    const value = e.target.value;
    setExtraData(prev => ({ ...prev, [fieldKey]: { ...prev[fieldKey], [name]: value } }));
  }, []);

  const onChangeWorkingHoursSwitch = useCallback(e => {
    if (e.target.checked) {
      onChangeExtraData("working_hours", { target: { name: "working_hours_type", value: "in_hours" } });
    } else {
      // The reference hours only apply to "in_hours"; drop them so they don't linger in the mapping.
      setExtraData(prev => {
        const { default_working_hours, ...rest } = prev.working_hours || {};
        return { ...prev, working_hours: { ...rest, working_hours_type: "in_percentage" } };
      });
    }
  }, [onChangeExtraData]);

  const handleCheckOverwrite = useCallback((field, checked) => {
    setOverwriteFields(prev => {
      const newOverwriteFields = { ...prev };
      if (checked) {
        newOverwriteFields[field] = true;
      } else {
        delete newOverwriteFields[field];
      }
      return newOverwriteFields;
    });
  }, []);

  const handleFieldMapping = async () => {
    if (!fieldMapping) {
      setFieldMappingError(translate("user_import.field_mapping_empty"));
      window.scrollTo(0, 0);
      return;
    }

    setLoading(true);

    const scopeFields = [];
    for (const f in fieldMapping) {
      if (fieldMapping[f]) scopeFields.push({
        key: f
      });
    }
    if (scopeFields.length > 0) {
      try {
        await putImportScope(scopeFields, false);
      } catch (err) {
        setFieldMappingError(errorMessage(err, "handleFieldMapping"));
        window.scrollTo(0, 0);
        setLoading(false);
        return;
      }
    }

    const fieldMappingData = [];

    for (const item of availableScopeFields) {
      let field = item.key;

      let header = fieldMapping?.[field];

      if (item.required && !header) {
        setFieldMappingError(translate("user_import.required_field_empty"));
        window.scrollTo(0, 0);
        setLoading(false);
        return;
      }

      if (!header) continue;

      const data = { field, header, overwrite: overwriteFields[field] || false };
      if (extraData[field]) {
        data.extra_data = extraData[field];
      }

      if (field === 'working_hours' && !data?.extra_data?.working_hours_type) {
        data.extra_data = {
          working_hours_type: 'in_percentage'
        };
      }

      if (field.includes("date") && !extraData?.[field]) {
        data.extra_data = { date_format: dateFormats[Object.keys(dateFormats)[0]] };
      }

      if (availableModules.length === 1 || (item?.section !== 'state_assessment_data' || !duplicatedFieldsOnState.includes(field))) {
        fieldMappingData.push(data);
      }
    }

    if (fieldMappingData.length === 0) {
      setFieldMappingError(translate("user_import.field_mapping_empty"));
      window.scrollTo(0, 0);
      setLoading(false);
      return;
    }

    try {
      const { data, status } = await api.post(`/employee-imports/${id}/field-mapping`, { field_mapping: fieldMappingData });
      if (status === 200) {
        // toast.success(translate("form.success.saved"));
        fetchImportRecords();
        setImportStatus(data.status);
        setFieldMappingError("");
        fetchImportData();
      }
    } catch (err) {
      setFieldMappingError(errorMessage(err, "handleFieldMapping"));
      window.scrollTo(0, 0);
    }
    setLoading(false);
  };

  const handleSubmitForProcessing = async () => {
    setLoading(true);
    try {
      const { status } = await api.post(`/employee-imports/${id}/process`);
      if (status === 200) {
        toast.success(translate("form.success.saved"));
        if (forTable) fetchUserImports();
        else fetchImportData();
      }
    } catch (err) {
      setError(errorMessage(err, "handleSubmitForProcessing"));
    }
    setLoading(false);
  };

  const putImportScope = async (arr, refetech = true) => {
    if (!importData?.import_scope) return;
    try {
      const { status } = await api.put(`/employee-imports/scopes/${importData?.import_scope?.hash_id}`, { fields: arr.map(f => f.key) });
      if (status === 200) {
        if (refetech) {
          toast.success(translate("form.success.saved"));
          fetchImportData();
        }
      }
    } catch (err) {
      throw err;
    }
  };

  const handleCancel = async () => {
    setLoading(true);
    try {
      const { status } = await api.post(`/employee-imports/${id}/cancel`);
      if (status === 200) {
        if (forTable) fetchUserImports();
        handleReturnToOverview();
      }
    } catch (err) {
      setError(errorMessage(err, "handleCancel"));
    }
    setLoading(false);
  };

  const handleBackToDraft = async () => {
    setLoading(true);
    try {
      const { status } = await api.post(`/employee-imports/${id}/draft`);
      if (status === 200) {
        fetchImportData();
      }
    } catch (err) {
      setError(errorMessage(err, "handleCancel"));
    }
    setLoading(false);
  };

  const handleChangeImportRecordsSortCol = useCallback(index => {
    const nextSort = importRecordsSortCol[0] === index
      ? [index, importRecordsSortCol[1] === "asc" ? "desc" : "asc"]
      : [index, "asc"];
    setImportRecordsSortCol(nextSort);
    setSelectedImportRecord(null);
    fetchImportRecords({ page: 1, sortCol: nextSort });
  }, [fetchImportRecords, importRecordsSortCol]);

  const handleChangeRecordView = useCallback(view => {
    setRecordView(view);
    setSelectedImportRecord(null);
    fetchImportRecords({ page: 1, view });
  }, [fetchImportRecords]);

  const handleChangeImportRecordsPage = useCallback(page => {
    setSelectedImportRecord(null);
    fetchImportRecords({ page });
  }, [fetchImportRecords]);

  const handleChangeImportRecordsPerPage = useCallback(perPage => {
    setSelectedImportRecord(null);
    setImportRecordsPagination(prev => ({ ...prev, perPage }));
    fetchImportRecords({ page: 1, perPage });
  }, [fetchImportRecords]);

  const formatLog = useCallback(log => {
    let newLog = log;
    if (log.toLowerCase().includes("date")) {
      for (const [frontFormat, backFormat] of Object.entries(dateFormats)) {
        newLog = newLog.replaceAll(backFormat, frontFormat);
      }
    }
    return newLog;
  }, []);

  const detailTabs = useMemo(() => ([
    { key: "details", label: editableTranslate("user_import.details", "Quellen-Details") },
    { key: "mapping", label: editableTranslate("user_import.field_assignment", "Zuordnung") },
    { key: "records", label: editableTranslate("user_import.records", "Datensätze") },
  ]), [editableTranslate]);

  const hasRecordRows = !loadingImportData && !loadingRecords && overviewImportRecords && importStatus !== "draft" && overviewImportRecordsTableHeaders.length > 2 && importRecordsPaged.length > 0;

  return (
    <>
      <div>
        {importData && !forTable && (
          <ImportDetailHeader
            backTo={backTo}
            backLabel={editableTranslate("user_import.back_to_employee_imports", "Zurück zu Mitarbeitenden-Importen")}
            iconClass="bi bi-people-fill"
            title={
              <>
                {editableTranslate("user_import.type_employees", "Mitarbeitende")} {editableTranslate("user_import.import", "Import")}
              </>
            }
            status={importStatus}
            statusLabel={editableTranslate("user_import.status." + importStatus, importData?.status_formatted)}
            metadata={[
              { label: editableTranslate("user_import.original_file_name", "Dateiname"), value: importData?.original_file_name },
              { label: editableTranslate("user_import.period", "Zeitraum"), value: importData?.period },
              { label: editableTranslate("user_import.source_type", "Quelltyp"), value: importData?.import_scope ? editableTranslate("user_import.source_type.file") : editableTranslate("user_import.source_type.predefined") },
              { label: editableTranslate("user_import.hash_id", "Hash ID"), value: importData?.hash_id },
              { label: editableTranslate("user_import.created_at", "Erstellt am"), value: formatDateTime(importData?.date_formatted?.created_at) },
            ]}
            stats={detailStats}
            progress={isProcessing ? importData?.progress ?? 0 : null}
            actions={importData?.conflict_resolution_capabilities?.can_export ? (
              <ImportReportMenu
                endpoint={`/employee-imports/${id}/report/export`}
                fallbackFilename="employee_import_report"
                rejectedFilter={issueImportRecordsCount > 0 ? { statuses: ["failed", "conflicted"], resolution: "unresolved" } : null}
                rejectedLabel={editableTranslate("import_report.rejected_with_conflicts", "Nur fehlerhafte Zeilen und offene Konflikte")}
                onError={setError}
              />
            ) : null}
          />
        )}

        {importData && (
          <div className={forTable ? "" : "mt-4"}>
            {error && (
              <div className="mt-3">
                <ErrorAlert error={error} onClose={() => setError(null)} />
              </div>
            )}

            {skippedImportRecordsCount > 0 && (
              <div className="alert alert-warning">
                {skippedImportRecordsCount}&nbsp;
                {
                  skippedImportRecordsCount === 1 ?
                    editableTranslate("user_import.skipped_record_singular", "Datensatz wurde") :
                    editableTranslate("user_import.skipped_record_plural", "Datensätze wurden")
                }&nbsp;
                {
                  editableTranslate("user_import.skipped_record_message", "während des Imports übersprungen (möglicherweise weil Überschreiben auf 'Nein' gesetzt ist).")
                }
              </div>
            )}

            {!forTable && (
              <ImportDetailTabs
                className="mb-4"
                tabs={detailTabs}
                activeTab={activeDetailTab}
                onChange={handleDetailTabChange}
              />
            )}

            {forTable ?
              <>
                <div className="gray-box-3">
                  <div className="row">
                    <div className="col">
                      <span>
                        <span className="text2-color">{editableTranslate("user_import.source_type", "Quelltyp")}:</span>
                        <span className="text-body-color ml-3" data-tooltip-id="import-hash-id-tooltip" data-tooltip-content={translate("user_import.hash_id", "Hash ID") + ": " + importData?.hash_id}>{importData?.import_scope ? editableTranslate("user_import.source_type.file") : editableTranslate("user_import.source_type.predefined")} ({importData?.original_file_name})</span>
                      </span>
                    </div>
                  </div>
                </div>
              </> : null}

            {(forTable || activeDetailTab === "details") && !forTable && (
              <div className="gray-box">
                <label className="question-label font-weight-bold">{editableTranslate("user_import.details", "Details")}</label>
                <div className="px-3 py-2 pb-3">
                  <div className="row">
                    <div className="col-md-4">
                      <div className="form-group">
                        <label className="peb-field__label">{editableTranslate("user_import.original_file_name", "Dateiname")}</label>
                        <p>{importData?.original_file_name}</p>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="form-group">
                        <label className="peb-field__label">{editableTranslate("user_import.source_type", "Quelltyp")}</label>
                        <p>{importData?.import_scope ? editableTranslate("user_import.source_type.file") : editableTranslate("user_import.source_type.predefined")}</p>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="form-group">
                        <label className="peb-field__label">{editableTranslate("user_import.hash_id", "Hash ID")}</label>
                        <p>{importData?.hash_id}</p>
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-3">
                      <div className="form-group">
                        <label className="peb-field__label">{editableTranslate("user_import.period", "Zeitraum")}</label>
                        <p data-testid="import-detail-period">{importData?.period || '-'}</p>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="form-group">
                        <label className="peb-field__label">{editableTranslate("user_import.records", "Datensätze")}</label>
                        <p>{importData?.imported_records} / {importData?.total_records} {editableTranslate("user_import.imported", "importiert")}</p>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="form-group">
                        <label className="peb-field__label">{editableTranslate("user_import.created_at", "Erstellt am")}</label>
                        <p>{formatDateTime(importData?.date_formatted.created_at)}</p>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="form-group">
                        <label className="peb-field__label">{editableTranslate("user_import.status", "Status")}</label>
                        <p>
                          <span className={"badge " + (["completed", "partially_completed"].includes(importData?.status) ? "badge-success" : importData?.status === "failed" ? "badge-danger" : "badge-warning")}>
                            {editableTranslate("user_import.status." + importData?.status, importData?.status_formatted)}
                          </span>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {(forTable || activeDetailTab === "mapping") && importStatus === "draft" && (
              <div>
                <hr />
                <div className="mb-4">
                  <h4 className="mb-1">{editableTranslate("user_import.import_wizard.use_previous_mapping", "Vorherige Zuordnung verwenden")}</h4>
                  <div className="mb-3">{editableTranslate("user_import.import_wizard.use_previous_mapping_description", "Möchten Sie dieselbe Feldzuordnung wie bei einem vorherigen Import verwenden?")}</div>
                  <PreviousMappingSelect importData={importData} imports={previousImports} selectedPreviousImport={selectedPreviousImport} setSelectedPreviousImport={setSelectedPreviousImport} />
                </div>

                {loadingImportData && (
                  <div className="text-center py-2" data-testid="loading">
                    <div className="spinner-border" role="status">
                      <span className="sr-only">Wird geladen...</span>
                    </div>
                  </div>
                )}

                {!loadingImportData && (
                  <>
                    {fieldMappingError && (
                      <div className="mt-1">
                        <ErrorAlert error={fieldMappingError} onClose={() => setFieldMappingError("")} />
                      </div>
                    )}

                    <DndContext
                      onDragStart={(e) => setActiveId(e.active.id)}
                      onDragEnd={(e) => {
                        setActiveId(null);
                        handleDragEnd(e);
                      }}>
                      <div className="import-headers-div">
                        <div className="d-flex justify-content-between align-items-center">
                          <span className="font-weight-bold">{editableTranslate('user_import.available_fields_drag', 'Verfügbare Felder (zum Zuordnen ziehen)')}</span>
                          <Button variant="secondary" onClick={handleAutoAssign} data-testid="auto-assign-button">{editableTranslate('user_import.automatically_assign', '✨ Automatisch zuordnen')}</Button>
                        </div>
                        <div className="d-flex flex-wrap gap-2 mt-3">
                          {
                            (importData?.headers || []).map(header => <Draggable key={header} id={`source-${header}`} originalId={header} fieldMapping={fieldMapping} />)
                          }
                        </div>
                      </div>

                      {
                        groupedScopeFields.map(({ section, fields }) => <GroupedScopeField key={section} section={section} fields={fields}
                          fieldMapping={fieldMapping}
                          handleManualRemove={handleManualRemove}
                          onChangeWorkingHoursSwitch={onChangeWorkingHoursSwitch}
                          dateFormats={dateFormats}
                          importStatus={importStatus}
                          extraData={extraData}
                          onChangeExtraData={onChangeExtraData}
                          overwriteFields={overwriteFields}
                          handleCheckOverwrite={handleCheckOverwrite}
                          availableModules={availableModules} />)
                      }

                      <DragOverlay dropAnimation={null}>
                        {activeId ? <Draggable id={activeId} originalId={parseDraggableId(activeId)?.originalId} /> : null}
                      </DragOverlay>
                    </DndContext>

                    <div className="alert alert-info">
                      <span className="d-block font-weight-bold text-big mb-2">{editableTranslate('user_import.mapping_progress', 'Zuordnungsfortschritt')}</span>
                      <span dangerouslySetInnerHTML={{ __html: sanitize(editableTranslateString('user_import.x_of_y_columns_mapped', '$X von $Y Spalten zugeordnet').replace('$X', reverseFieldMapping.length).replace('$Y', importData?.headers?.length)) }} />
                    </div>
                  </>
                )}
              </div>
            )}

            {(forTable || activeDetailTab === "mapping") && importStatus !== "draft" && (
              <div className="import-field-assignment">
                <span>{editableTranslate('user_import.field_assignment', 'Feldzuordnungen')}</span>
                {reverseFieldMapping.length > 0 ? (
                  <table className="table" data-testid="reverse-field-mapping-table">
                    <tbody>
                      {
                        reverseFieldMapping.map((item, i) => <tr key={i}>
                          <td className="w-30percent">{item.header}</td>
                          <td className="w-40percent text-center"><i className="bi bi-arrow-right"></i></td>
                          <td className="w-30percent text-right">{item.fields.join(', ')}</td>
                        </tr>)
                      }
                    </tbody>
                  </table>
                ) : (
                  <div className="alert alert-light border mt-3 mb-0" data-testid="empty-state">
                    {editableTranslate("global.no_data", "Keine Daten")}
                  </div>
                )}
              </div>
            )}

            {(forTable || activeDetailTab === "records") && !loadingImportData && !loadingRecords && importStatus !== "draft" && issueImportRecordsCount > 0 && (
              <div className="mb-4" data-testid="import-record-recovery">
                <Banner
                  intent={unresolvedConflictCount > 0 ? "warning" : "info"}
                  title={unresolvedConflictCount > 0
                    ? editableTranslate("user_import.conflicts.banner_title", `${unresolvedConflictCount} offene Konflikte`)
                    : editableTranslate("user_import.rejected.banner_title", "Nicht importierte Datensätze")}
                >
                  {editableTranslate(
                    "user_import.conflicts.banner_body_report",
                    "Gültige Zeilen wurden bereits importiert. Prüfen Sie Namenskonflikte hier. Andere fehlerhafte Zeilen können Sie über „Bericht herunterladen“ → „Nur fehlerhafte Zeilen und offene Konflikte“ exportieren, korrigieren und erneut importieren."
                  )}
                </Banner>
              </div>
            )}

            {(forTable || activeDetailTab === "records") && !loadingImportData && !loadingRecords && importStatus !== "draft" && overviewImportRecords && (
              <FilterPillGroup
                className="mb-3"
                label={editableTranslate("user_import.records_filter.label", "Import-Ergebnisse filtern")}
                value={recordView}
                onChange={handleChangeRecordView}
                testIdPrefix="import-record-"
                options={[
                  {
                    value: "all",
                    label: editableTranslate("user_import.records_filter.all", "Alle Ergebnisse"),
                  },
                  {
                    value: "issues",
                    label: <>{editableTranslate("user_import.records_filter.issues", "Fehler und Konflikte")} ({issueImportRecordsCount})</>,
                  },
                  ...(importData?.conflicted_records_count > 0 ? [{
                    value: "unresolved",
                    label: <>{editableTranslate("user_import.records_filter.unresolved", "Offene Konflikte")} ({unresolvedConflictCount})</>,
                  }] : []),
                ]}
              />
            )}

            {(forTable || activeDetailTab === "records") && !loadingImportData && loadingRecords && (
              <div className="text-center py-2" data-testid="loading">
                <div className="spinner-border" role="status">
                  <span className="sr-only">Wird geladen...</span>
                </div>
              </div>
            )}

            {(forTable || activeDetailTab === "records") && !loadingImportData && !loadingRecords && importStatus === "draft" && (
              <div className="alert alert-light border">
                {editableTranslate("user_import.records_available_after_processing", "Die Datensätze werden hier angezeigt, nachdem der Import verarbeitet wurde.")}
              </div>
            )}

            {(forTable || activeDetailTab === "records") && hasRecordRows && (
              <>
                <ImportRecordsTable
                  importStatus={importStatus}
                  overviewImportRecordsTableHeaders={overviewImportRecordsTableHeaders}
                  importRecordsSortCol={importRecordsSortCol}
                  handleChangeImportRecordsSortCol={handleChangeImportRecordsSortCol}
                  importRecordsPaged={importRecordsPaged}
                  selectedImportRecord={selectedImportRecord}
                  setSelectedImportRecord={setSelectedImportRecord}
                  importRecordErrorFields={importRecordErrorFields}
                  importRecordMetadata={importRecordMetadata}
                  detailedImportTableData={detailedImportTableData}
                  detailedImportTableHeaders={detailedImportTableHeaders}
                  formatLog={formatLog}
                  resolvingRecordHash={resolvingRecordHash}
                  onResolveConflict={handleRequestConflictResolution}
                  conflictResolutionCapabilities={importData?.conflict_resolution_capabilities}
                />
                <ListPagination
                  page={importRecordsPagination.page}
                  perPage={importRecordsPagination.perPage}
                  totalPages={importRecordsPagination.totalPages}
                  totalRows={importRecordsPagination.totalRows}
                  setPage={handleChangeImportRecordsPage}
                  setPerPage={handleChangeImportRecordsPerPage}
                  className="mt-4"
                />
              </>
            )}

            {(forTable || activeDetailTab === "records") && !loadingImportData && !loadingRecords && importStatus !== "draft" && !hasRecordRows && (
              <div className="alert alert-light border" data-testid="empty-state">
                {editableTranslate("global.no_data", "Keine Daten")}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="text-center mt-3">
        {importStatus !== "ready_for_import" && !forTable && (
          <Button data-testid="back-button" variant="secondary" onClick={handleReturnToOverview} loading={loading || loadingImportData || loadingRecords}>
            {editableTranslate(importStatus === "draft" ? "button.cancel" : "button.back", importStatus === "draft" ? "Abbrechen" : "Zurück")}
          </Button>
        )}
        {importStatus === "draft" && (
          <Button data-testid="save-mapping-button" variant="primary" onClick={handleFieldMapping} loading={loading || loadingImportData || loadingRecords} className="ml-2">{editableTranslate("user_import.save_mapping", "Zuordnung speichern")}</Button>
        )}
        {importStatus === "ready_for_import" && (
          <>
            <Button variant="secondary" onClick={handleCancel} disabled={loading || loadingImportData || loadingRecords} data-testid="cancel-button" className="ml-2">{editableTranslate("button.cancel")}</Button>
            <Button variant="secondary" onClick={handleBackToDraft} disabled={loading || loadingImportData || loadingRecords} data-testid="back-button" className="ml-2">
              {editableTranslate("button.back")}
            </Button>
            <Button data-testid="process-button" variant="primary" onClick={handleSubmitForProcessing} loading={loading || loadingImportData || loadingRecords} className="ml-2">{editableTranslate("user_import.process", "Verarbeiten")}</Button>
          </>
        )}
      </div>

      <Modal
        open={!!resolutionDialog}
        title={resolutionDialog?.resolution === "update"
          ? editableTranslate("user_import.conflict.confirm_update_title", "Importierte Daten übernehmen?")
          : editableTranslate("user_import.conflict.ignore_title", "Konflikt als geprüft markieren?")}
        okLabel={resolutionDialog?.resolution === "update"
          ? editableTranslate("user_import.conflict.confirm_update", "Importierte Daten übernehmen")
          : editableTranslate("user_import.conflict.ignore_confirm", "Als geprüft markieren")}
        cancelLabel={editableTranslate("button.cancel", "Abbrechen")}
        loading={!!resolvingRecordHash}
        onConfirm={handleConfirmConflictResolution}
        onClose={() => setResolutionDialog(null)}
        testIdPrefix="import-conflict-"
      >
        {resolutionDialog?.resolution === "update"
          ? editableTranslate("user_import.conflict.confirm_update_body", "Die vollständige Importzeile wird erneut geprüft und gemäß der ursprünglichen Überschreib-Einstellungen auf die vorhandene Person angewendet. Diese Entscheidung wird protokolliert.")
          : editableTranslate("user_import.conflict.ignore_body", "Es werden keine Beschäftigtendaten geändert. Der ursprüngliche Konflikt bleibt in der Importhistorie sichtbar und wird als geprüft markiert.")}
      </Modal>

      <ReactTooltip id="import-hash-id-tooltip" place="bottom" clickable={true} />

      <ReactTooltip id="import-also-for-other-module-tooltip" place="bottom" content={translate('tooltips.user_import.also_for_other_module')} />
    </>
  );
}
